import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Play, RefreshCw } from "lucide-react";

/**
 * Reviews — Cycle Admin. Create a 6-monthly review cycle, generate the
 * per-person instances (auto-resolving crafts + reviewers from live work),
 * and drill into a cycle's instances. Admin/Manager gated at the route.
 */

const BAND_COLORS = {
  HERO: "#16a34a", STRONG: "#2563eb", AVERAGE: "#d97706", RETHINK: "#dc2626",
};

function bandChip(band) {
  if (!band || !band.label) return <span style={{ color: "var(--t3)" }}>—</span>;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: BAND_COLORS[band.label] || "#666" }}>
      {band.emoji} {band.label}
    </span>
  );
}

export default function ReviewsAdmin() {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [instances, setInstances] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ label: "", period_start: "", period_end: "" });
  const [busy, setBusy] = useState(false);

  const loadCycles = async () => {
    setLoading(true);
    try {
      const res = await api.get("/reviews/cycles");
      setCycles(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load cycles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCycles(); }, []);

  const loadInstances = async (cycle) => {
    setSelected(cycle);
    try {
      const res = await api.get(`/reviews/cycles/${cycle.cycle_id}/instances`);
      setInstances(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load instances");
    }
  };

  const createCycle = async () => {
    if (!form.label.trim() || !form.period_start || !form.period_end) {
      return toast.error("Label and both dates are required");
    }
    setBusy(true);
    try {
      await api.post("/reviews/cycles", form);
      toast.success("Cycle created");
      setShowCreate(false);
      setForm({ label: "", period_start: "", period_end: "" });
      loadCycles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create cycle");
    } finally {
      setBusy(false);
    }
  };

  const generate = async (cycle) => {
    if (!window.confirm(`Generate review instances for "${cycle.label}"? This scans everyone's shipped work for the period.`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/reviews/cycles/${cycle.cycle_id}/generate`);
      toast.success(`Created ${res.data.created} reviews · skipped ${res.data.skipped}`);
      loadCycles();
      loadInstances(cycle);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    } finally {
      setBusy(false);
    }
  };

  const shareAll = async (cycle) => {
    if (!window.confirm(`Share every review in "${cycle.label}" with its subject? Reviews recompute first, then each person is notified and can see their scorecard.`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/reviews/cycles/${cycle.cycle_id}/share-all`);
      toast.success(`Shared ${res.data.shared} reviews`);
      loadCycles();
      loadInstances(cycle);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dm-page-container" style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--t3)", fontSize: 13, marginBottom: 16, background: "none", border: "none", cursor: "pointer" }}>
        <ArrowLeft size={16} /> BACK
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800 }}>Review Cycles</h1>
          <p style={{ color: "var(--t3)", fontSize: 13, marginTop: 4 }}>
            6-monthly, platform-synced. Section B pulls each person's real shipped work — no re-entry.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", background: "#1a56db", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
          <Plus size={16} /> New cycle
        </button>
      </div>

      {showCreate && (
        <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: "var(--t3)", textTransform: "uppercase" }}>Label</label>
              <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="H1 2026 — Jan–Jun" className="form-input" style={{ width: "100%", padding: 10, marginTop: 4, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t1)" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "var(--t3)", textTransform: "uppercase" }}>Period start</label>
              <input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} style={{ width: "100%", padding: 10, marginTop: 4, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t1)" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "var(--t3)", textTransform: "uppercase" }}>Period end</label>
              <input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} style={{ width: "100%", padding: 10, marginTop: 4, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t1)" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button disabled={busy} onClick={createCycle} style={{ padding: "8px 16px", background: "#1a56db", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Create</button>
            <button onClick={() => setShowCreate(false)} style={{ padding: "8px 16px", background: "var(--s2)", color: "var(--t2)", border: "1px solid var(--bdr)", borderRadius: 8, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: "var(--t3)" }}>Loading…</p>
      ) : cycles.length === 0 ? (
        <p style={{ color: "var(--t3)" }}>No cycles yet. Create your first review cycle.</p>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {cycles.map((c) => (
            <div key={c.cycle_id} style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{c.label}</div>
                  <div style={{ fontSize: 12, color: "var(--t3)", marginTop: 2 }}>
                    {c.period_start} → {c.period_end} · <span style={{ textTransform: "uppercase" }}>{c.status}</span> · {c.instance_count} reviews
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button disabled={busy} onClick={() => generate(c)} title="Generate / refresh instances" style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 12px", background: "var(--s2)", color: "var(--t1)", border: "1px solid var(--bdr)", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
                    <Play size={14} /> Generate
                  </button>
                  <button disabled={busy} onClick={() => shareAll(c)} title="Recompute + share every review with its subject" style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 12px", background: "rgba(22,163,74,.14)", color: "#16a34a", border: "1px solid rgba(22,163,74,.3)", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
                    <RefreshCw size={14} /> Share all
                  </button>
                  <button onClick={() => loadInstances(c)} style={{ padding: "8px 12px", background: "#1a56db", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
                    View
                  </button>
                </div>
              </div>

              {selected?.cycle_id === c.cycle_id && (
                <div style={{ marginTop: 16, borderTop: "1px solid var(--bdr)", paddingTop: 12 }}>
                  {instances.length === 0 ? (
                    <p style={{ color: "var(--t3)", fontSize: 13 }}>No instances yet — click Generate.</p>
                  ) : (
                    <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ color: "var(--t3)", textAlign: "left" }}>
                          <th style={{ padding: "6px 8px" }}>Person</th>
                          <th style={{ padding: "6px 8px" }}>Crafts</th>
                          <th style={{ padding: "6px 8px" }}>Status</th>
                          <th style={{ padding: "6px 8px" }}>Composite</th>
                          <th style={{ padding: "6px 8px" }}>Band</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {instances.map((inst) => (
                          <tr key={inst.instance_id} style={{ borderTop: "1px solid var(--bdr)" }}>
                            <td style={{ padding: "8px" }}>{inst.subject_name || inst.subject_id}</td>
                            <td style={{ padding: "8px", color: "var(--t3)" }}>{(inst.crafts || []).join(", ")}</td>
                            <td style={{ padding: "8px", textTransform: "uppercase", fontSize: 11, color: "var(--t3)" }}>{inst.status}</td>
                            <td style={{ padding: "8px", fontWeight: 700 }}>{inst.composite != null ? inst.composite.toFixed(2) : "—"}</td>
                            <td style={{ padding: "8px" }}>{bandChip(inst.band)}</td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              <button onClick={() => navigate(`/reviews/instance/${inst.instance_id}`)} style={{ color: "#1a56db", background: "none", border: "none", cursor: "pointer", fontSize: 13 }}>Open →</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
